/**
 * Global-market seeker-Pro catalog for the Buy Me a Coffee (BMaC) rail.
 *
 * Dormant until configured. BMaC has no custom-metadata at checkout, so linkage
 * is email-based. A single BMaC membership level ("Loker Dollar Pro") carries
 * BOTH a monthly ($9) and a yearly ($59) price; the webhook distinguishes the two
 * productKeys by the recurring `duration_type` ("month"/"year") that BMaC sends —
 * confirmed against the live payload, which carries no membership_level_id and an
 * ambiguous `amount` unit, making duration the reliable signal.
 *   - checkout: a single BMaC membership URL (BMAC_TIER_URL) — buyer picks the
 *     monthly/yearly period on BMaC.
 *   - webhook:  duration_type → productKey (see productKeyForBmacDuration).
 *
 * USD prices are fixed here (the IDR price-ladder experiment stays Mayar-only).
 * Only seeker keys are eligible — employer/tool purchases never route to BMaC
 * (AUP guardrail). `pro_annual_founding` is an ID-only promo and is excluded.
 */

import type { SeekerProductKey } from "@/lib/mayar/products";

/** Seeker keys offered on the global (BMaC) rail. */
export type GlobalSeekerKey = Extract<
	SeekerProductKey,
	"pro_monthly" | "pro_annual"
>;

const GLOBAL_KEYS: readonly GlobalSeekerKey[] = ["pro_monthly", "pro_annual"];

export function isGlobalSeekerKey(value: string): value is GlobalSeekerKey {
	return (GLOBAL_KEYS as readonly string[]).includes(value);
}

/**
 * Fixed USD price per key, in minor units (cents).
 *
 * Cross-subsidy pricing: the global market (USD earners — higher buying power)
 * pays a deliberately higher price than Indonesia so that margin can fund the
 * low IDR prices for the home market. It is still a no-brainer for someone
 * earning USD:
 *   - Indonesia (Mayar): pro_monthly ≈ Rp39k (~$2.4), pro_annual ≈ Rp299k (~$18).
 *   - Global (BMaC):     pro_monthly  $9   (~3.7×),    pro_annual  $59 (~45% off
 *                        vs monthly — the annual nudge), both far below typical
 *                        career-SaaS Pro tiers ($15–30/mo).
 * Single worldwide price (not per-country) — matches the one-global-variant
 * decision; per-region PPP can layer on later without touching this rail.
 */
export const GLOBAL_USD_MINOR: Record<GlobalSeekerKey, number> = {
	pro_monthly: 900, // $9.00 / mo
	pro_annual: 5900, // $59.00 / yr  (≈ $4.92/mo — 45% off monthly)
};

/**
 * The single BMaC membership checkout URL (the "Loker Dollar Pro" level page).
 * Null when unconfigured (→ the global checkout returns `billing_unavailable`).
 * The buyer chooses monthly/yearly there.
 *
 * Reads the Cloudflare Worker env first (the authoritative source for wrangler
 * `vars` at runtime), then falls back to `process.env` (local dev / tests).
 * Reading ONLY `process.env` was a latent activation bug: wrangler `vars` do not
 * reliably surface on `process.env` in the OpenNext Workers runtime, so the URL
 * would resolve to null even after it was configured. This mirrors `getAppUrl()`
 * in checkout.ts and the webhook's secret read (`process.env.X || cfEnv.X`).
 */
export function bmacMembershipUrl(env?: {
	BMAC_TIER_URL?: string | null;
}): string | null {
	return (
		env?.BMAC_TIER_URL?.trim() || process.env.BMAC_TIER_URL?.trim() || null
	);
}

/**
 * The Gumroad membership checkout URL (the "Loker Dollar Pro" product page).
 * Gumroad is the PRIMARY global rail (chosen over BMaC for real subscription
 * webhooks + access-gating); the buyer picks monthly/yearly on the product page
 * and the webhook grants by the `recurrence` Gumroad sends. Null when
 * unconfigured, so the checkout falls back to BMaC, then `billing_unavailable`.
 *
 * Same env-read order as `bmacMembershipUrl()` — Worker `vars` first (the
 * authoritative runtime source), then `process.env` (local dev / tests).
 */
export function gumroadMembershipUrl(env?: {
	GUMROAD_TIER_URL?: string | null;
}): string | null {
	return (
		env?.GUMROAD_TIER_URL?.trim() ||
		process.env.GUMROAD_TIER_URL?.trim() ||
		null
	);
}

/**
 * Resolve a BMaC recurring `duration_type` to our productKey — the unambiguous
 * monthly/annual signal BMaC sends (`"month"` / `"year"`), confirmed against the
 * live webhook payload. Far more robust than guessing by amount (BMaC's `amount`
 * units are ambiguous, and there is no `membership_level_id` in the payload).
 * Any other value returns null and is not granted.
 */
export function productKeyForBmacDuration(
	durationType: string | null | undefined,
): GlobalSeekerKey | null {
	const d = durationType?.trim().toLowerCase();
	if (d === "month" || d === "monthly") return "pro_monthly";
	if (d === "year" || d === "yearly" || d === "annual") return "pro_annual";
	return null;
}
